import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import { Card } from '@ovComponents/2-component/card/card';
import {
  DataTable,
  type DataTableTanstackTable,
  type Row,
  type RowsPerPageOption,
} from '@ovComponents/2-component/dataTable';
import { ConfirmationModal } from '@ovComponents/3-pattern/confirmationDialog/confirmationDialog';
import type { Theme } from 'hephaestus/core';
import { Box, Button, Typography } from 'hephaestus/web';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ReconFlagRow } from '../utils/reconFlags.types';
export type ReconFlagsGridCardProps = {
  theme: Theme;
  table: DataTableTanstackTable<ReconFlagRow>;
  pageSize: RowsPerPageOption;
  tableLoading: boolean;
  pageCount: number;
  page: number;
  onPageChange: (page: number) => void;
  totalRowCount: number;
  onPerPageChange: (perPage: number) => void;
  onRowClick: (row: ReconFlagRow) => void;
  selectedCount: number;
  onSelectAllOnPage: () => void;
  onClearSelection: () => void;
  onBulkDismiss: () => boolean | Promise<boolean>;
  bulkDismissLoading: boolean;
  gridQueryLoading: boolean;
  hasActiveFilters: boolean;
};

export const ReconFlagsGridCard = ({
  theme,
  table,
  pageSize,
  tableLoading,
  pageCount,
  page,
  onPageChange,
  totalRowCount,
  onPerPageChange,
  onRowClick,
  selectedCount,
  onSelectAllOnPage,
  onClearSelection,
  onBulkDismiss,
  bulkDismissLoading,
  gridQueryLoading,
  hasActiveFilters,
}: ReconFlagsGridCardProps) => {
  const { t } = useTranslation(['reconciliation']);
  const [bulkDismissConfirmOpen, setBulkDismissConfirmOpen] = useState(false);

  useEffect(() => {
    if (selectedCount === 0) {
      setBulkDismissConfirmOpen(false);
    }
  }, [selectedCount]);

  const handleConfirmBulkDismiss = (): void => {
    void (async () => {
      const succeeded = await onBulkDismiss();
      if (succeeded) {
        setBulkDismissConfirmOpen(false);
      }
    })();
  };

  const showSelectAllOnPage = table
    .getRowModel()
    .rows.some((row) => row.getCanSelect() && !row.getIsSelected());

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
      }}
    >
      {selectedCount > 0 && (
        <Card
          variant='outlined'
          sx={{
            backgroundColor: theme.color.primaryVariant,
            borderColor: theme.color.primaryVariantPressed,
            boxShadow: 'none',
          }}
        >
          <Box
            sx={{
              py: 1,
              pr: 1,
              pl: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <Typography
              variant='bodyMedium'
              sx={{ color: theme.color.onSurface, fontWeight: 600 }}
            >
              {t('reconFlags.selection.count', { count: selectedCount })}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 1,
              }}
            >
              {showSelectAllOnPage && (
                <Button
                  variant='outlined'
                  color='primary'
                  size='small'
                  onClick={onSelectAllOnPage}
                  disabled={bulkDismissLoading}
                >
                  {t('reconFlags.selection.selectAllOnPage')}
                </Button>
              )}
              <Button
                variant='outlined'
                color='primary'
                size='small'
                onClick={onClearSelection}
                disabled={bulkDismissLoading}
              >
                {t('reconFlags.selection.clear')}
              </Button>
              <Button
                variant='contained'
                color='primary'
                size='small'
                loading={bulkDismissLoading}
                disabled={bulkDismissLoading}
                onClick={() => setBulkDismissConfirmOpen(true)}
              >
                {t('reconFlags.selection.dismiss')}
              </Button>
            </Box>
          </Box>
        </Card>
      )}
      <ConfirmationModal
        open={bulkDismissConfirmOpen}
        title={t('reconFlags.selection.dismissConfirmTitle')}
        bodyText={t('reconFlags.selection.dismissConfirmBody')}
        confirmButtonLabel={t('reconFlags.selection.dismissConfirmAction')}
        confirmButtonColor='primary'
        loading={bulkDismissLoading}
        onConfirm={handleConfirmBulkDismiss}
        onCancel={() => setBulkDismissConfirmOpen(false)}
        testIdPrefix='recon-flags-bulk-dismiss'
      />

      <Card loading={gridQueryLoading}>
        <DataTable
          table={table}
          rowsPerPage={pageSize}
          loading={tableLoading}
          emptyContent={
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                py: 6,
                gap: 0.5,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <InfoRoundedIcon sx={{ mr: 1 }} />
                <Typography variant='bodyLarge' sx={{ fontWeight: 600 }}>
                  {t(
                    hasActiveFilters
                      ? 'reconFlags.emptyTable'
                      : 'reconFlags.emptyTableNoResults',
                  )}
                </Typography>
              </Box>
              <Typography
                variant='bodyMedium'
                sx={{ fontWeight: 500 }}
                color='textSecondary'
              >
                {t(
                  hasActiveFilters
                    ? 'reconFlags.emptyTableHint'
                    : 'reconFlags.emptyTableNoData',
                )}
              </Typography>
            </Box>
          }
          getRowProps={(row: Row<ReconFlagRow>) => ({
            hover: true,
            pointer: true,
            onClick: () => onRowClick(row.original),
          })}
          tablePagination={{
            mode: 'controlled',
            ovVariant: 'tableFooter',
            pageCount,
            page,
            onPageChange,
            totalRowCount,
            perPage: pageSize,
            onPerPageChange,
            showPerPageSelect: true,
            summaryNamespace: 'reconciliation',
            summaryKey: 'reconFlags.pagination.summary',
          }}
        />
      </Card>
    </Box>
  );
};
